/**
 * Calendar date math on integer day numbers (days since 1970-01-01) and wall-clock
 * milliseconds — never on `Date` local arithmetic, so DST in the browser's zone can't shift
 * a day (SCHEDULING.md §1 "Identical everywhere"). Only `wallClock` / `zonedToUtc` touch tzdata.
 */

export const DAY_MS = 86_400_000
export const MINUTE_MS = 60_000

/** `YYYY-MM-DD` → day number, or null when malformed / not a real date. */
export function parseDay(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const ms = Date.UTC(y, mo - 1, d)
  const back = new Date(ms)
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null
  return Math.floor(ms / DAY_MS)
}

/** Day number → `YYYY-MM-DD`. */
export function formatDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10)
}

/** ISO weekday index, 0 = Monday … 6 = Sunday (1970-01-01 was a Thursday). */
export function isoWeekday(day: number): number {
  return (((day + 3) % 7) + 7) % 7
}

export function dayParts(day: number): { year: number; month: number; date: number } {
  const d = new Date(day * DAY_MS)
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, date: d.getUTCDate() }
}

export function startOfWeek(day: number): number {
  return day - isoWeekday(day)
}

/** Monday-first week containing `day`: 7 day numbers. */
export function weekDays(day: number): number[] {
  const start = startOfWeek(day)
  return Array.from({ length: 7 }, (_, i) => start + i)
}

export function firstOfMonth(day: number): number {
  const { year, month } = dayParts(day)
  return Math.floor(Date.UTC(year, month - 1, 1) / DAY_MS)
}

export function addMonths(day: number, n: number): number {
  const { year, month } = dayParts(day)
  return Math.floor(Date.UTC(year, month - 1 + n, 1) / DAY_MS)
}

/** Monday-first month grid: whole weeks covering the month (5 or 6 rows, ≤ 42 days). */
export function monthGrid(day: number): number[][] {
  const first = firstOfMonth(day)
  const last = addMonths(day, 1) - 1
  const rows: number[][] = []
  for (let start = startOfWeek(first); start <= last; start += 7) rows.push(Array.from({ length: 7 }, (_, i) => start + i))
  return rows
}

export type CalendarView = 'week' | 'month' | 'day'

/** Inclusive local-date range to request for a view anchored on `day` (always ≤ 62 days). */
export function viewRange(view: CalendarView, day: number): { from: number; to: number } {
  if (view === 'day') return { from: day, to: day }
  if (view === 'week') {
    const start = startOfWeek(day)
    return { from: start, to: start + 6 }
  }
  const rows = monthGrid(day)
  return { from: rows[0]![0]!, to: rows[rows.length - 1]![6]! }
}

/** Anchor after "previous" / "next". */
export function shiftAnchor(view: CalendarView, day: number, dir: -1 | 1): number {
  if (view === 'day') return day + dir
  if (view === 'week') return day + 7 * dir
  return addMonths(day, dir)
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

export function weekdayShort(day: number): string {
  return WEEKDAYS_SHORT[isoWeekday(day)]!
}

export function monthShort(day: number): string {
  return MONTHS_SHORT[dayParts(day).month - 1]!
}

/** Toolbar title: "October 2026", "5 – 11 Oct 2026", "28 Sep – 4 Oct 2026", "Thu 8 Oct 2026". */
export function rangeTitle(view: CalendarView, day: number): string {
  if (view === 'month') {
    const { year, month } = dayParts(day)
    return `${MONTHS[month - 1]} ${year}`
  }
  if (view === 'day') {
    const p = dayParts(day)
    return `${weekdayShort(day)} ${p.date} ${MONTHS_SHORT[p.month - 1]} ${p.year}`
  }
  const { from, to } = viewRange('week', day)
  const a = dayParts(from)
  const b = dayParts(to)
  if (a.year !== b.year) return `${a.date} ${MONTHS_SHORT[a.month - 1]} ${a.year} – ${b.date} ${MONTHS_SHORT[b.month - 1]} ${b.year}`
  if (a.month !== b.month) return `${a.date} ${MONTHS_SHORT[a.month - 1]} – ${b.date} ${MONTHS_SHORT[b.month - 1]} ${b.year}`
  return `${a.date} – ${b.date} ${MONTHS_SHORT[b.month - 1]} ${b.year}`
}

/* -------------------------------------------------------------------- zones */

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
    formatters.set(zone, f)
  }
  return f
}

/** Wall clock of instant `ms` in `zone`, as ms since 1970-01-01T00:00 of the local calendar (SCHEDULING.md §4.2). */
export function wallClock(ms: number, zone: string): number {
  const truncated = Math.floor(ms / 1000) * 1000
  const parts: Record<string, number> = {}
  for (const p of formatter(zone).formatToParts(new Date(truncated))) if (p.type !== 'literal') parts[p.type] = Number(p.value)
  const wall = Date.UTC(parts.year!, parts.month! - 1, parts.day!, parts.hour === 24 ? 0 : parts.hour!, parts.minute!, parts.second!)
  return wall + (ms - truncated)
}

/** First instant whose wall clock in `zone` is ≥ `wall` (overlap → first repetition, gap → transition). */
export function zonedToUtc(wall: number, zone: string): number {
  const offsets = new Set([-DAY_MS, 0, DAY_MS].map((d) => wallClock(wall + d, zone) - (wall + d)))
  const exact = [...offsets].map((o) => wall - o).filter((u) => wallClock(u, zone) === wall)
  if (exact.length > 0) return Math.min(...exact)
  // Gap: binary-search the first instant at or after `wall`.
  const os = [...offsets]
  let lo = wall - Math.max(...os)
  let hi = wall - Math.min(...os)
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2)
    if (wallClock(mid, zone) >= wall) hi = mid
    else lo = mid
  }
  return hi
}

/** Today's local date (day number) in `zone`. */
export function todayIn(zone: string, now: number = Date.now()): number {
  return Math.floor(wallClock(now, zone) / DAY_MS)
}

/** `HH:MM` of a wall-clock ms value. */
export function wallTime(wall: number): string {
  const t = ((wall % DAY_MS) + DAY_MS) % DAY_MS
  const m = Math.floor(t / MINUTE_MS)
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** Parses a zone-less local datetime (`2026-10-08T11:00:00[.sss]`) to wall-clock ms. */
export function parseWall(value: string | null | undefined): number | null {
  if (!value) return null
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(value)
  if (!m) return null
  const day = parseDay(m[1]!)
  if (day === null) return null
  return day * DAY_MS + Number(m[2]) * 3_600_000 + Number(m[3]) * MINUTE_MS + Number(m[4] ?? 0) * 1000 + Number((m[5] ?? '0').padEnd(3, '0'))
}

/** Wall-clock ms → `YYYY-MM-DDTHH:mm` (for `datetime-local` inputs). */
export function formatWallInput(wall: number): string {
  return `${formatDay(Math.floor(wall / DAY_MS))}T${wallTime(wall)}`
}
